'use client';

import Paginator from '@/layers/presentation/components/common/Paginator';
import ProgramCard from '@/layers/presentation/components/programs/ProgramCard';
import { Card, CardBody, CardFooter, CardHeader } from '@/layers/presentation/components/ui/Card';
import { useSubscriptionModal } from '@/layers/presentation/components/ui/SubscriptionPrompt';
import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useProgramsList } from '@/layers/presentation/hooks';
import { useTranslation } from '@/layers/presentation/i18n/client';
import {
  getPatientParamBySlug,
  getPatientParamValuesExtended,
} from '@/layers/presentation/services/patients';
import logger from '@/layers/support/monitoring/logger';
import { ProgramStatus, UserType } from '@/types/modelEnums';
import { Program } from '@/types/models';
import { ProgramFilters } from '@/types/programs';
import { Button, Skeleton, Spinner } from '@nextui-org/react';
import { useRouter } from 'next/navigation';
import React, { useEffect, useMemo } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / ProgramsList]';

type ProgramsListProps = {
  type: UserType | 'about';
  filters: ProgramFilters;
  onSaveSearch: (params: any, query: string) => void;
  showDefaultRecent?: boolean;
  ready?: boolean;
};

function ProgramsList({
  type,
  filters,
  showDefaultRecent = false,
  ready = true,
}: ProgramsListProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, patientParamsDictionary } = useGlobalState();

  const memoizedFilters = useMemo(
    () => ({
      ...filters,
      ...(type !== UserType.SPONSOR ? { status: ProgramStatus.ACTIVE } : {}),
      ...({ sortBy: 'added_date', sortOrder: 'desc' } as ProgramFilters),
    }),
    [filters, type],
  );

  const {
    programs,
    isEaLoading,
    isNonEaLoading,
    isInitialLoad,
    eaError,
    nonEaError,
    isLoading,
    isRefining,
  } = useProgramsList(memoizedFilters, showDefaultRecent, ready);

  const selectedCancerTypeParam = useMemo(() => {
    if (!patientParamsDictionary.length || !filters.patientParams) return null;
    return getPatientParamBySlug(patientParamsDictionary, 'c3_cancer_type');
  }, [patientParamsDictionary, filters.patientParams]);

  const selectedCancerTypeValues = useMemo(() => {
    if (!selectedCancerTypeParam || !filters.patientParams || isEaLoading || isNonEaLoading)
      return null;
    const extendedValues = getPatientParamValuesExtended(
      patientParamsDictionary,
      filters.patientParams,
    );
    return extendedValues[selectedCancerTypeParam.id]?.values || [];
  }, [
    selectedCancerTypeParam,
    filters.patientParams,
    patientParamsDictionary,
    isEaLoading,
    isNonEaLoading,
  ]);

  const hasFilters = useMemo(() => {
    return Object.values(filters.patientParams || {}).some(
      param => Array.isArray(param) && param.length > 0,
    );
  }, [filters.patientParams]);

  const hasQuery = useMemo(() => {
    return filters.query !== '';
  }, [filters.query]);

  const typeUrl = type.toLowerCase();

  useEffect(() => {
    logger.debug(`${LOG_MODULE} Render`, {
      type,
      user,
      programs,
      isEaLoading,
      isNonEaLoading,
      eaError,
      nonEaError,
      filters,
      memoizedFilters,
      hasFilters,
      hasQuery,
      selectedCancerTypeParam,
      selectedCancerTypeValues,
    });
  }, [
    type,
    user,
    programs,
    isEaLoading,
    isNonEaLoading,
    eaError,
    nonEaError,
    filters,
    memoizedFilters,
    hasFilters,
    hasQuery,
    selectedCancerTypeParam,
    selectedCancerTypeValues,
  ]);

  const handleCardPress = (id: number) => {
    router.push(`/${typeUrl}/programs/${id}`);
  };

  // Setup subscription modal data
  const subscriptionText = useMemo(
    () => ({
      title: 'Stay Updated on Treatment Opportunities',
      text: 'Get notified when new expanded access programs become available that match your criteria.',
      button: 'Subscribe for Updates',
    }),
    [],
  );

  const subscriptionParams = useMemo(
    () => ({
      userType: type !== 'about' ? type : undefined,
      patientParams: filters.patientParams,
      query: filters.query,
    }),
    [type, filters.patientParams, filters.query],
  );

  const { openModal } = useSubscriptionModal();

  return (
    <div className="grid grid-cols-1 gap-8">
      {isInitialLoad && (
        <div className="mt-12 grid grid-cols-1 gap-8">
          {Array.from({ length: 2 }).map((_, index) => (
            <Card key={index} radius="md" isPressable>
              <CardHeader>
                <Skeleton className="w-3/4 rounded-lg">
                  <p>{t('components:programsList.loading')}</p>
                </Skeleton>
              </CardHeader>
              <CardBody>
                <Skeleton className="rounded-lg">
                  <p>{t('components:programsList.loading')}</p>
                </Skeleton>
              </CardBody>
              <CardFooter>
                <Skeleton className="w-1/4 rounded-lg">
                  <p>{t('components:programsList.loading')}</p>
                </Skeleton>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {!isInitialLoad && (
        <>
          {/* EA Section */}
          <div>
            <div className="mt-8 mb-4 flex items-center gap-3">
              <h2 className="m-0" data-testid="ea-section-heading">
                {hasQuery
                  ? t('components:programsList.bestMatches')
                  : t('components:programsList.recentExpandedAccess')}
              </h2>
              {hasQuery && isRefining && (
                <span
                  className="flex items-center gap-2 text-sm text-default-500"
                  data-testid="refining-indicator"
                  role="status"
                  aria-live="polite"
                >
                  <Spinner size="sm" color="default" />
                  {t('components:programsList.refining')}
                </span>
              )}
            </div>
            {isEaLoading && (
              <div className="flex justify-center py-6">
                <Spinner size="lg" color="primary" role="status" />
              </div>
            )}
            {!isEaLoading && programs.ea && programs.ea.length > 0 ? (
              <Paginator<Program> list={programs.ea} pageSize={10}>
                {paginatedItems => (
                  <div className="flex flex-col gap-8" data-testid="ea-programs-list">
                    {paginatedItems.map(program => (
                      <ProgramCard
                        key={program.id}
                        program={program}
                        type={type}
                        typeUrl={typeUrl}
                        onCardPress={handleCardPress}
                        selectedCancerType={selectedCancerTypeValues?.[0]?.label}
                      />
                    ))}
                  </div>
                )}
              </Paginator>
            ) : !isEaLoading ? (
              <Card>
                <CardBody>
                  <div className="flex flex-col gap-4">
                    <div>{t('components:programsList.noProgramsFound')}</div>
                    <div>
                      However, Right2Hope will continue searching for new treatment opportunities on
                      your behalf. Enter your email, and we&apos;ll notify you as soon as a relevant
                      program becomes available.
                    </div>
                    <Button
                      color="primary"
                      className="inline-flex w-fit max-w-max"
                      onPress={() => openModal(subscriptionText, subscriptionParams)}
                    >
                      Get Treatment Updates Straight to Your Email
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ) : null}
          </div>

          {/* Non-EA Section */}
          <div className="mt-8">
            <h2 className="mb-4" data-testid="trials-section-heading">
              {hasQuery
                ? t('components:programsList.otherPrograms')
                : t('components:programsList.recentClinicalTrials')}
            </h2>
            {isNonEaLoading && (
              <div className="flex justify-center py-6">
                <Spinner size="lg" color="primary" role="status" />
              </div>
            )}
            {!isNonEaLoading && programs.nonEa && programs.nonEa.length > 0 ? (
              <Paginator<Program> list={programs.nonEa} pageSize={10}>
                {paginatedItems => (
                  <div className="flex flex-col gap-8" data-testid="trials-programs-list">
                    {paginatedItems.map(program => (
                      <ProgramCard
                        key={program.id}
                        program={program}
                        type={type}
                        typeUrl={typeUrl}
                        onCardPress={handleCardPress}
                        selectedCancerType={selectedCancerTypeValues?.[0]?.label}
                      />
                    ))}
                  </div>
                )}
              </Paginator>
            ) : !isNonEaLoading ? (
              <Card>
                <CardBody data-testid="no-trials">
                  {t('components:programsList.noTrialsFound')}
                </CardBody>
              </Card>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default React.memo(ProgramsList);
